import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, patch } from "../../lib/request"

// Fetch tone of voice settings for a client
export const fetchToneOfVoiceSettings = createAsyncThunk(
  "toneOfVoice/fetchSettings",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await get(`client-tone-of-voice-settings/clients/${clientId}`)
      return response
    } catch (error) {
      if (error.message && (error.message.includes("404") || error.message.includes("Not Found"))) {
        return null // Return null for 404, not an error
      }
      return rejectWithValue(error.message)
    }
  },
  {
    condition: (clientId, { getState }) => {
      if (clientId == null || clientId === "") return false
      const slice = getState().toneOfVoice ?? {}
      const same = String(slice.fetchedForClientId ?? "") === String(clientId)
      if (slice.loading) return false
      if (same && slice.fetchedForClientId != null) return false
      return true
    },
  },
)

// Create new tone of voice settings
export const createToneOfVoiceSettings = createAsyncThunk(
  "toneOfVoice/createSettings",
  async ({ clientId, settings }, { rejectWithValue }) => {
    try {
      const response = await post(`client-tone-of-voice-settings/clients/${clientId}`, settings)
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

// Update existing tone of voice settings
export const updateToneOfVoiceSettings = createAsyncThunk(
  "toneOfVoice/updateSettings",
  async ({ clientId, settings }, { rejectWithValue }) => {
    try {
      const response = await post(`client-tone-of-voice-settings/clients/${clientId}`, settings)
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  },
)

const defaultSliders = {
  length: 1,
  complexity: 1,
  formality: 1,
  enthusiasm: 1,
  creativity: 1,
}

const initialState = {
  settings: null,
  hasExistingSettings: false,
  loading: false,
  error: null,
  /** Last clientId whose fetch/create/update result is reflected (avoids stale tone from another property). */
  fetchedForClientId: null,
  // Default slider values (1-3 scale)
  sliderValues: {
    length: 1,
    complexity: 1,
    formality: 1,
    enthusiasm: 1,
    creativity: 1,
  },
  useWords: [],
  avoidWords: [],
  customText: "",
}

const toneOfVoiceSlice = createSlice({

  name: "toneOfVoice",
  initialState,
  reducers: {
    updateSliderValue: (state, action) => {
      const { slider, value } = action.payload
      state.sliderValues[slider] = value
    },
    updateCustomText: (state, action) => {
      state.customText = action.payload
    },
    updateUseWords: (state, action) => {
      state.useWords = action.payload
    },
    updateAvoidWords: (state, action) => {
      state.avoidWords = action.payload
    },
    resetSettings: (state) => {
      state.settings = null
      state.hasExistingSettings = false
      state.fetchedForClientId = null
      state.sliderValues = { ...defaultSliders }
      state.useWords = []
      state.avoidWords = []
      state.customText = ""
      state.error = null
    },
    /** Clears form only; keeps fetched settings + hasExistingSettings so Save still updates the client. */
    resetToneFormToDefaults: (state) => {
      state.sliderValues = { ...defaultSliders }
      state.useWords = []
      state.avoidWords = []
      state.customText = ""
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch settings
      .addCase(fetchToneOfVoiceSettings.pending, (state, action) => {
        const nextId = action.meta.arg
        state.loading = true
        state.error = null
        if (String(state.fetchedForClientId ?? "") !== String(nextId ?? "")) {
          state.hasExistingSettings = false
          state.settings = null
          state.sliderValues = { ...defaultSliders }
          state.useWords = []
          state.avoidWords = []
          state.customText = ""
        }
        if (nextId != null) state.fetchedForClientId = nextId
      })
      .addCase(fetchToneOfVoiceSettings.fulfilled, (state, action) => {
        state.loading = false
        state.fetchedForClientId = action.meta.arg ?? null
        if (action.payload && action.payload.id) {
          const settings = action.payload
          state.settings = settings
          state.hasExistingSettings = true
          state.sliderValues = {
            length: settings.length || 1,
            complexity: settings.complexity || 1,
            formality: settings.formality || 1,
            enthusiasm: settings.emojiUsage || 1,
            creativity: settings.energyLevel || 1,
          }
          state.useWords = settings.useWords || []
          state.avoidWords = settings.avoidWords || []
          state.customText = settings.customGuidelines || ""
        } else {
          // No existing settings (404 or null response), use defaults
          state.hasExistingSettings = false
          state.settings = null
          state.sliderValues = {
            length: 1,
            complexity: 1,
            formality: 1,
            enthusiasm: 1,
            creativity: 1,
          }
          state.useWords = []
          state.avoidWords = []
          state.customText = ""
        }
      })
      .addCase(fetchToneOfVoiceSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.fetchedForClientId = action.meta.arg ?? null
        state.hasExistingSettings = false
        state.settings = null
        state.sliderValues = { ...defaultSliders }
        state.useWords = []
        state.avoidWords = []
        state.customText = ""
      })
      // Create settings
      .addCase(createToneOfVoiceSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createToneOfVoiceSettings.fulfilled, (state, action) => {
        state.loading = false
        state.settings = action.payload
        state.hasExistingSettings = true
      })
      .addCase(createToneOfVoiceSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update settings
      .addCase(updateToneOfVoiceSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateToneOfVoiceSettings.fulfilled, (state, action) => {
        state.loading = false
        state.settings = action.payload
        state.hasExistingSettings = true
        const cid = action.meta?.arg?.clientId
        if (cid != null && cid !== "") {
          state.fetchedForClientId = cid
        }
      })
      .addCase(updateToneOfVoiceSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const {
  updateSliderValue,
  updateCustomText,
  updateUseWords,
  updateAvoidWords,
  resetSettings,
  resetToneFormToDefaults,
} = toneOfVoiceSlice.actions
export default toneOfVoiceSlice.reducer
